import { useToast } from "@/components/ui/use-toast";
import {
  AUTO_CLEANUP_THRESHOLD_MS,
  CALL_SERVICE_ENDPOINTS,
  VIDEO_CALL_END_REASONS,
  type VideoCallEndReason,
} from "@/constants/video-call";
import { getCloudOptimizedRoomConfig } from "@/utils/livekitCloudUtils";
import { getEndedReasonForDisconnect } from "@/utils/network-detection";
import {
  CallConfig,
  CallState,
  ConversationMessage,
  Lead,
} from "@/types/video-call";
import {
  ConnectionState,
  RemoteParticipant,
  Room,
  RoomEvent,
  VideoPresets,
} from "livekit-client";
import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useOrganization } from "../../App";
import { NetworkErrorLogger, logConnectionStateChange } from "@/utils/networkErrorLogger";
import { logCategorizedError, logNetworkStateChange } from "@/utils/errorCategorization";
import { filterTrackingPhrases } from "@/utils/transcriptFiltering";
import { apiServerHeaders } from "@/lib/apiServerHeaders";
import { CallService } from "@/services/video-call/callService";

interface UseVideoCallProps {
  leadId: string;
  lead: Lead | null;
  selectedAssistantId: string;
  callConfig: CallConfig;
  isInterviewSession?: boolean;
  interviewDetails?: any;
  encodedParams?: string;
}

export const useVideoCall = ({
  leadId,
  lead,
  selectedAssistantId,
  callConfig,
  isInterviewSession = false,
  interviewDetails = null,
  encodedParams = null,
}: UseVideoCallProps) => {
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  // Call state
  const [callState, setCallState] = useState<CallState>({
    loading: false,
    connecting: false,
    connected: false,
    callActive: false,
    error: null,
    callId: null,
  });

  // LiveKit state
  const [room, setRoom] = useState<Room | null>(null);
  const [remoteParticipants, setRemoteParticipants] = useState<
    RemoteParticipant[]
  >([]);

  // Conversation state
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const [currentSubtitle, setCurrentSubtitle] = useState<string>("");
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [hasAgentSpoken, setHasAgentSpoken] = useState(false);
  const [isTranscriptionPaused, setIsTranscriptionPaused] = useState(false);

  // Network state
  const [networkStatus, setNetworkStatus] = useState<'connected' | 'disconnected'>('connected');
  const [connectionQuality, setConnectionQuality] = useState<'excellent' | 'good' | 'poor' | 'unknown'>('unknown');
  
  // Network monitoring refs
  const networkDisconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastValidConnectionRef = useRef<number>(Date.now());

  // Refs
  const hasAutoStarted = useRef(false);
  const currentStreamIdRef = useRef<string | null>(null);
  const subtitleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const speakingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Connection state guards to prevent multiple simultaneous connections
  const isConnectingRef = useRef(false);
  const connectionAttemptTimestampRef = useRef<number>(0);
  const lastConnectionAttemptRef = useRef<number>(0);

  const getCallServiceUrl = useCallback(() => {
    return import.meta.env.VITE_CALL_SERVICE_URL || "http://localhost:8000";
  }, []);

  // Delayed network status change to avoid false positives - tuned for LiveKit Cloud
  const setNetworkStatusWithDelay = useCallback((status: 'connected' | 'disconnected', reason: string) => {
    // Clear any existing timeout
    if (networkDisconnectTimeoutRef.current) {
      clearTimeout(networkDisconnectTimeoutRef.current);
      networkDisconnectTimeoutRef.current = null;
    }

    if (status === 'disconnected') {
      logConnectionStateChange('disconnected', {
        component: 'useVideoCall',
        reason,
        metadata: {
          callId: callState.callId,
          waitTime: '10 seconds (LiveKit Cloud)'
        }
      });
      // Wait longer for LiveKit Cloud to avoid false positives during region switches
      networkDisconnectTimeoutRef.current = setTimeout(async () => {
        Sentry.logger.warn(`Confirmed network disconnection after delay`, { component: 'useVideoCall', reason });
        setNetworkStatus('disconnected');
        
        // End the call with NETWORK_CONNECTION_LOST reason after retries are exhausted
        if (callState.callId && callState.callActive) {
          Sentry.logger.warn(`Network connection lost after retries - ending call`, { 
            component: 'useVideoCall', 
            callId: callState.callId 
          });
          
          try {
            await CallService.endCall(
              callState.callId, 
              currentOrganization?.id || "", 
              VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST
            );
            
            // Disconnect from LiveKit room if still connected
            if (room && room.state !== "disconnected") {
              room.removeAllListeners();
              room.disconnect();
            }
            
            // Reset call state
            setCallState((prev) => ({
              ...prev,
              connected: false,
              callActive: false,
              callId: null,
              error: "Network connection lost"
            }));
            setRoom(null);
            setRemoteParticipants([]);
          } catch (error) {
            Sentry.logger.error("Error ending call after network loss:", { error, component: 'useVideoCall' });
          }
        }
      }, 10000); // Increased from 5s to 10s for Cloud to handle longer reconnection attempts
    } else {
      Sentry.logger.info(`Network restored`, { component: 'useVideoCall', reason });
      lastValidConnectionRef.current = Date.now();
      setNetworkStatus('connected');
    }
  }, [callState.callId, callState.callActive, currentOrganization?.id, room]);

  const clearNetworkTimeout = useCallback(() => {
    if (networkDisconnectTimeoutRef.current) {
      clearTimeout(networkDisconnectTimeoutRef.current);
      networkDisconnectTimeoutRef.current = null;
    }
  }, []);

  // Clear network timeout when component unmounts
  useEffect(() => {
    return () => {
      clearNetworkTimeout();
      // Clear timeout refs on cleanup
      if (subtitleTimeoutRef.current) {
        clearTimeout(subtitleTimeoutRef.current);
      }
      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current);
      }
    };
  }, [clearNetworkTimeout]);

  const handleRemoteParticipant = useCallback(
    (participant: RemoteParticipant) => {
      Sentry.logger.info("Participant connected", {
        component: 'useVideoCall',
        identity: participant.identity,
        sid: participant.sid,
        metadata: participant.metadata,
        isAgent:
          participant.identity.includes("agent") ||
          participant.identity.includes("voiceagent"),
      });
    },
    []
  );

  const handleTranscriptionReceived = useCallback(
    (
      segments: any[], // TranscriptionSegment[]
      participant?: RemoteParticipant,
      publication?: any // TrackPublication
    ) => {
      try {
        // Skip processing if transcription is paused
        if (isTranscriptionPaused) {
          Sentry.logger.debug("Transcription is paused, skipping processing", {
            component: 'useVideoCall'
          });
          return;
        }

        // Determine if this is agent speaking
        const isAgent =
          participant?.identity?.includes("agent") ||
          participant?.identity?.includes("assistant");


        // Process each transcription segment
        segments?.forEach((segment) => {
          const rawText = segment.text || "";
          // Clean transcript text by removing ** and - symbols
          const text = rawText.replace(/\*\*/g, '').replace(/-/g, '');
          const isFinal = segment.final !== false;

          // Update subtitle immediately - ONLY for agent speech (filter tracking phrases)
          if (isAgent && text) {
            const filteredText = filterTrackingPhrases(text);
            // Only set subtitle if there's content left after filtering
            if (filteredText.trim()) {
              setCurrentSubtitle(filteredText);

              // Clear subtitle after a delay
              if (isFinal) {
                // Clear any existing subtitle timeout
                if (subtitleTimeoutRef.current) {
                  clearTimeout(subtitleTimeoutRef.current);
                }
                subtitleTimeoutRef.current = setTimeout(() => {
                  setCurrentSubtitle("");
                }, 2000);
              }
            }
          }

          // Update agent speaking state
          if (isAgent && text) {
            setIsAgentSpeaking(true);

            // Mark that agent has spoken and set connected to true
            if (!hasAgentSpoken) {
              setHasAgentSpoken(true);
              setCallState((prev) => ({
                ...prev,
                connecting: false,
                connected: true,
              }));
            }

            // Clear any existing speaking timeout
            if (speakingTimeoutRef.current) {
              clearTimeout(speakingTimeoutRef.current);
            }
            speakingTimeoutRef.current = setTimeout(() => {
              setIsAgentSpeaking(false);
            }, 1500);
          }

          // Add to conversation if final
          if (isFinal && text.trim()) {
            const role = isAgent ? "assistant" : "user";
            // Filter tracking phrases from assistant messages before adding to conversation
            const messageText = isAgent ? filterTrackingPhrases(text.trim()) : text.trim();
            
            // Only add message if there's content left after filtering
            if (messageText.trim()) {
              const newMessage: ConversationMessage = {
                role,
                message: messageText,
                timestamp: new Date().toISOString(),
              };

              setConversation((prev) => {
                const lastMessage = prev[prev.length - 1];
                if (
                  lastMessage &&
                  lastMessage.message === newMessage.message &&
                  lastMessage.role === newMessage.role
                ) {
                  return prev;
                }
                return [...prev, newMessage];
              });
            }
          }
        });
      } catch (error) {
        Sentry.logger.error("Error handling TranscriptionReceived event", {
          error,
          component: 'useVideoCall'
        });
      }
    },
    [isTranscriptionPaused, hasAgentSpoken]
  );

  const setupRoomEventListeners = useCallback(
    (newRoom: Room) => {
      // Create a ref to track transcription paused state inside event handlers
      let transcriptionPausedRef = { current: false };
      // Note: ConnectionError is not available in current LiveKit version
      // Connection errors are handled through the Connected/Disconnected events

      newRoom.on(RoomEvent.Connected, async () => {
        Sentry.logger.info("Connected to room successfully", {
          component: 'useVideoCall',
          roomState: newRoom.state,
          localParticipant: newRoom.localParticipant.identity,
          connectionTime: Date.now() - (window as any).roomConnectionStart
        });

        // Reset network status when successfully connected
        setNetworkStatusWithDelay('connected', 'LiveKit Room Connected');

        // Only set callActive to true, but keep connected false until agent speaks
        setCallState((prev) => ({
          ...prev,
          callActive: true,
        }));
      });

      // Listen for connection state changes to detect network issues more reliably
      newRoom.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
        Sentry.logger.debug("Connection state changed", {
          component: 'useVideoCall',
          state
        });
        
        switch (state) {
          case ConnectionState.Connecting:
            // Don't reset to connected here, could be reconnecting due to network issues
            Sentry.logger.debug("Room connecting/reconnecting", {
              component: 'useVideoCall'
            });
            break;
          case ConnectionState.Connected:
            Sentry.logger.info("Room fully connected - resetting network status", {
              component: 'useVideoCall'
            });
            setNetworkStatusWithDelay('connected', 'LiveKit Connected');
            lastValidConnectionRef.current = Date.now(); // Reset successful connection time
            
            // Clear any paused state when fully connected
            if (transcriptionPausedRef.current) {
              Sentry.logger.info("Clearing paused state after reconnection", {
                component: 'useVideoCall'
              });
              transcriptionPausedRef.current = false;
              setIsTranscriptionPaused(false);
            }
            break;
          case ConnectionState.Disconnected:
            // Only set disconnected if not intentionally paused
            if (!transcriptionPausedRef.current) {
              setNetworkStatusWithDelay('disconnected', 'LiveKit ConnectionState Disconnected');
              Sentry.logger.warn("Network disconnected detected via ConnectionStateChanged", {
                component: 'useVideoCall'
              });
            }
            break;
          case ConnectionState.Reconnecting:
            // Don't show reconnecting state if we're just paused
            if (!transcriptionPausedRef.current) {
              setNetworkStatusWithDelay('disconnected', 'LiveKit ConnectionState Reconnecting');
              Sentry.logger.warn("Network reconnecting detected - showing network lost indicator", {
                component: 'useVideoCall'
              });
            }
            break;
        }
      });

      // Listen for connection quality changes (can indicate network issues)
      newRoom.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
        if (participant === newRoom.localParticipant) {
          Sentry.logger.debug("Local connection quality changed", {
            component: 'useVideoCall',
            quality
          });
          
          // Update connection quality state for UI
          setConnectionQuality(quality as 'excellent' | 'good' | 'poor' | 'unknown');
          
          // If quality drops to poor, it might indicate network issues
          if (quality === 'poor' && networkStatus === 'connected') {
            Sentry.logger.warn("Poor connection quality detected", {
              component: 'useVideoCall'
            });
            // Don't immediately set to disconnected, but log for monitoring
          }
        }
      });

      // Listen for reconnection events
      newRoom.on(RoomEvent.Reconnecting, () => {
        logConnectionStateChange('reconnecting', {
          component: 'useVideoCall',
          reason: 'LiveKit room reconnecting',
          metadata: {
            roomState: newRoom.state,
            callId: callState.callId
          }
        });
        setNetworkStatusWithDelay('disconnected', 'LiveKit Reconnecting Event');
      });

      newRoom.on(RoomEvent.Reconnected, () => {
        logConnectionStateChange('connected', {
          component: 'useVideoCall',
          reason: 'LiveKit room reconnected successfully',
          metadata: {
            callId: callState.callId
          }
        });
        setNetworkStatusWithDelay('connected', 'LiveKit Reconnected Event');
      });

      newRoom.on(RoomEvent.Disconnected, async (reason) => {
        console.error(`[useVideoCall] Room disconnected: ${reason}`, {
          roomState: newRoom.state,
          callId: callState.callId,
          networkStatus
        });

        // Log room disconnection with categorized error handling
        logCategorizedError(new Error(`Room disconnected: ${reason}`), {
          component: 'useVideoCall',
          operation: 'roomDisconnected',
          callId: callState.callId,
          organizationId: currentOrganization?.id,
          metadata: {
            reason,
            roomState: newRoom.state,
            networkStatus,
            transcriptionPaused: transcriptionPausedRef.current,
            wasUserInitiated: String(reason) === "USER_INITIATED"
          }
        });
        
        // If we're in a paused state and get a disconnection, it might be a false positive
        if (transcriptionPausedRef.current && String(reason) !== "USER_INITIATED") {
          Sentry.logger.info("Ignoring disconnect during paused state", {
            component: 'useVideoCall',
            reason
          });
          // Don't process the disconnection if we're paused
          return;
        }
        
        // Convert reason to string for comparison
        const reasonStr = String(reason);
        Sentry.logger.debug("Disconnect was user initiated check", {
          component: 'useVideoCall',
          userInitiated: reasonStr === "USER_INITIATED",
          reason: reasonStr
        });

        const timeSinceConnection =
          Date.now() - (window as any).roomConnectionStart;
        Sentry.logger.debug("Time since room connection started", {
          component: 'useVideoCall',
          timeSinceConnection
        });

        // Update network status - show network lost for non-user initiated disconnects
        if (reasonStr !== "USER_INITIATED") {
          console.log("🔗 Setting network status to disconnected via Disconnected event, reason:", reasonStr);
          setNetworkStatusWithDelay('disconnected', `LiveKit Disconnected: ${reasonStr}`);
        } else {
          console.log("🔗 User initiated disconnect, keeping network status as is");
        }

        setCallState((prev) => ({
          ...prev,
          connected: false,
          callActive: false,
        }));
        setRoom(null);
        setRemoteParticipants([]);

        // Use utility function to determine ended reason based on disconnect reason and network status
        const endedReason = getEndedReasonForDisconnect(reasonStr, networkStatus);
        
        if (endedReason === VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST) {
          Sentry.logger.warn("Network issue detected - using NETWORK_CONNECTION_LOST", { 
            reason: reasonStr, 
            networkStatus,
            component: 'useVideoCall'
          });
        }

        // Auto cleanup for non-user-initiated disconnects - LiveKit Cloud aware
        if (reasonStr !== "USER_INITIATED" && callState.callId) {
          console.log("⚠️ Non-user-initiated disconnect detected on LiveKit Cloud");
          console.log("Disconnect reason was:", reason);
          console.log("Time since connection:", timeSinceConnection, "ms");

          // Increased threshold for LiveKit Cloud as connections can take longer to establish
          const CLOUD_CLEANUP_THRESHOLD_MS = 12000; // 12 seconds for Cloud to allow for network reconnection
          
          if (timeSinceConnection > CLOUD_CLEANUP_THRESHOLD_MS) {
            console.log("🧹 Calling auto cleanup API (LiveKit Cloud connection lasted > 12s)");
            try {
              // Don't update status from browser - the Python agent will handle it
              // The agent detects disconnects and sets proper ended_reason

              CallService.endCall(callState.callId, currentOrganization?.id || "", endedReason).catch((err) => 
                console.error("Error in auto cleanup:", err)
              );
            } catch (error) {
              console.error("Error calling cleanup API on disconnect:", error);
            }
          } else {
            console.log(
              "⏱️ Skipping auto cleanup - LiveKit Cloud connection was too brief (< 12s)"
            );
            console.log("This might be a normal Cloud agent startup disconnect or region switch");
          }
        } else {
          console.log(
            "✅ Not calling cleanup API - either user initiated or no callId"
          );
          console.log("Reason:", reason, "CallId:", callState.callId);
        }
      });

      newRoom.on(
        RoomEvent.ParticipantConnected,
        (participant: RemoteParticipant) => {
          console.log("🤖 Participant connected:", participant.identity);
          setRemoteParticipants((prev) => [...prev, participant]);
        }
      );

      newRoom.on(
        RoomEvent.ParticipantDisconnected,
        (participant: RemoteParticipant) => {
          console.log("Participant disconnected:", participant.identity);
          setRemoteParticipants((prev) =>
            prev.filter((p) => p.identity !== participant.identity)
          );
        }
      );

      // Handle transcription using the TranscriptionReceived event
      newRoom.on(RoomEvent.TranscriptionReceived, handleTranscriptionReceived);

      // Handle pause/resume commands through DataReceived (for control messages only)
      newRoom.on(
        RoomEvent.DataReceived,
        (payload: Uint8Array, participant?: RemoteParticipant) => {
          try {
            const data = JSON.parse(new TextDecoder().decode(payload));

            // Only handle control messages, not transcriptions
            if (data.type === "call_paused") {
              console.log("🔍 [PAUSE_RESUME_DEBUG] Call PAUSED:", {
                reason: data.reason || "unknown",
                timestamp: data.timestamp,
                source: "data_message",
                callId: callState.callId,
                roomState: newRoom?.state,
                userAgent: navigator.userAgent,
                platform: navigator.platform
              });
              console.log("Received call pause command, pausing transcription");
              transcriptionPausedRef.current = true;
              setIsTranscriptionPaused(true);
              setCurrentSubtitle("");
              setIsAgentSpeaking(false);
            } else if (data.type === "call_resumed") {
              console.log("🔍 [PAUSE_RESUME_DEBUG] Call RESUMED:", {
                timestamp: data.timestamp,
                source: "data_message",
                callId: callState.callId,
                roomState: newRoom?.state,
                previousPauseReason: data.previousReason || "unknown",
                timeSincePause: data.pauseDuration || "unknown",
                userAgent: navigator.userAgent,
                platform: navigator.platform
              });
              console.log("Received call resume command, resuming transcription");
              transcriptionPausedRef.current = false;
              setIsTranscriptionPaused(false);

              // Clear any pending network disconnect timeouts to prevent false disconnections
              clearNetworkTimeout();

              // Ensure network status is connected
              setNetworkStatusWithDelay('connected', 'Call Resumed');

              // Validate room connection state
              if (newRoom && newRoom.state === 'connected') {
                console.log("Room is connected, resume successful");
              } else {
                console.warn("Room not in connected state during resume, current state:", newRoom?.state);
              }
            }
          } catch (error) {
            console.log("Error processing data message:", error);
          }
        }
      );
    },
    [callState.callId, getCallServiceUrl, handleTranscriptionReceived, isTranscriptionPaused, clearNetworkTimeout, setNetworkStatusWithDelay, room]
  );

  const initializeVideoCall = useCallback(async () => {
    if (!lead || !selectedAssistantId) return;

    // For regular calls, require currentOrganization
    if (!isInterviewSession && !currentOrganization) return;

    // Enhanced connection state guards to prevent multiple simultaneous connections
    const now = Date.now();
    
    // Prevent multiple simultaneous connections
    if (callState.connecting || callState.connected || room) {
      console.log("Already connecting or connected, skipping initialization");
      return;
    }
    
    // Guard against rapid successive connection attempts
    if (isConnectingRef.current) {
      console.log("Connection attempt already in progress, skipping duplicate");
      return;
    }
    
    // Debouncing: Prevent connection attempts within 2 seconds of each other
    const timeSinceLastAttempt = now - lastConnectionAttemptRef.current;
    if (timeSinceLastAttempt < 2000) {
      console.log(`Connection attempt debounced - ${timeSinceLastAttempt}ms since last attempt (min 2000ms)`);
      return;
    }
    
    // Set connection guards
    isConnectingRef.current = true;
    connectionAttemptTimestampRef.current = now;
    lastConnectionAttemptRef.current = now;

    setCallState((prev) => ({
      ...prev,
      connecting: true,
      connected: false,
      error: null,
    }));
    
    // Reset agent spoken state for new call
    setHasAgentSpoken(false);

    try {
      const callServiceUrl = getCallServiceUrl();
      let response;

      if (isInterviewSession && encodedParams) {
        // For interview sessions, use /api/call with unique link
        response = await fetch(
          `${callServiceUrl}${CALL_SERVICE_ENDPOINTS.INITIATE}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              is_web_session: true,
              is_video: true,
              unique_link: encodedParams,
              metadata: {
                tab_change_detection: callConfig.monitoring.tabChangeDetection,
                external_monitor_detection:
                  callConfig.monitoring.externalMonitorDetection,
                blur_detection: callConfig.monitoring.blurDetection,
                viewport_detection: callConfig.monitoring.viewportDetection,
                // Note: For interview sessions, echo_cancellation comes from stored database settings
                // Don't send from frontend to avoid overriding database values
              },
            }),
          }
        );
      } else {
        // Regular video call: the signed-in user's login token (the interview link above is
        // its own credential)
        response = await fetch(
          `${callServiceUrl}${CALL_SERVICE_ENDPOINTS.INITIATE}`,
          {
            method: "POST",
            headers: await apiServerHeaders(),
            body: JSON.stringify({
              is_web_session: true,
              is_video: true,
              lead_id: leadId,
              assistant_id: selectedAssistantId,
              organization_id: currentOrganization?.id || "",
              metadata: {
                tab_change_detection: callConfig.monitoring.tabChangeDetection,
                external_monitor_detection:
                  callConfig.monitoring.externalMonitorDetection,
                blur_detection: callConfig.monitoring.blurDetection,
                viewport_detection: callConfig.monitoring.viewportDetection,
                echo_cancellation: callConfig.monitoring.echoCancellation,
              },
            }),
          }
        );
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API call failed: ${response.status} - ${errorText}`);
      }

      const tokenData = await response.json();
      console.log("🔊 [useVideoCall] Token data received:", tokenData);
      console.log("🔊 [useVideoCall] Raw response keys:", Object.keys(tokenData));
      console.log("🔊 [useVideoCall] audio_config from API:", tokenData.audio_config);
      console.log("🔊 [useVideoCall] audio_config type:", typeof tokenData.audio_config);
      console.log("🔊 [useVideoCall] Has audio_config property:", tokenData.hasOwnProperty('audio_config'));
      
      // Check for all possible audio config field names
      console.log("🔊 [useVideoCall] Checking all audio fields:");
      ['audio_config', 'audioConfig', 'audio_settings', 'audioSettings'].forEach(key => {
        console.log(`  ${key}:`, tokenData[key]);
      });

      const {
        livekit_url: serverUrl,
        livekit_token: token,
        room_name: roomName,
        call_id: responseCallId,
        audio_config: audioConfig,
        echoCancellation,
      } = tokenData;

      setCallState((prev) => ({ ...prev, callId: responseCallId }));

      if (!serverUrl || !token || !roomName) {
        throw new Error(
          `Missing required data - serverUrl: ${!!serverUrl}, token: ${!!token}, roomName: ${!!roomName}`
        );
      }

      console.log("LiveKit connection details:", { serverUrl, roomName });
      console.log("🔊 [Frontend] Full tokenData received from API:", tokenData);
      console.log("🔊 [Frontend] audio_config from API:", tokenData.audio_config);
      console.log("🔊 [Frontend] Extracted audioConfig:", audioConfig);
      console.log("🔊 [Frontend] Direct echoCancellation from API:", echoCancellation);

      // Create audio config from direct echoCancellation value if audioConfig is missing
      // For interview sessions, use the callConfig value if API doesn't provide it
      const finalAudioConfig = audioConfig || (echoCancellation !== undefined ? {
        echoCancellation,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 48000
      } : (isInterviewSession ? {
        echoCancellation: callConfig.monitoring.echoCancellation,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 48000
      } : undefined));
      
      console.log("🔊 [Frontend] isInterviewSession:", isInterviewSession);
      console.log("🔊 [Frontend] callConfig.monitoring.echoCancellation:", callConfig.monitoring.echoCancellation);
      console.log("🔊 [Frontend] Final audio config being used:", finalAudioConfig);

      // Create and configure room with LiveKit Cloud optimizations using API-provided audio config
      const cloudConfig = getCloudOptimizedRoomConfig(finalAudioConfig);
      console.log("🔊 [Frontend] cloudConfig created:", cloudConfig);
      const newRoom = new Room(cloudConfig);

      setupRoomEventListeners(newRoom);

      // Connect to room with LiveKit Cloud optimizations
      console.log("Attempting to connect to LiveKit Cloud with URL:", serverUrl);
      (window as any).roomConnectionStart = Date.now();

      try {
        // LiveKit Cloud connection with optimized settings
        await newRoom.connect(serverUrl, token, {
          // LiveKit Cloud specific optimizations
          autoSubscribe: true,
          maxRetries: 5, // Increased retries for better resilience
          peerConnectionTimeout: 15000, // 15s timeout for Cloud to handle network issues
        });
        
        // Wait for stable connection to LiveKit Cloud before proceeding
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('LiveKit Cloud connection stability timeout'));
          }, 12000); // 12s timeout for cloud stability to handle network issues
          
          const checkStableConnection = () => {
            if (newRoom.state === 'connected' && 
                newRoom.localParticipant.identity && 
                newRoom.engine?.client?.ws?.readyState === WebSocket.OPEN) {
              clearTimeout(timeout);
              resolve(true);
            } else {
              setTimeout(checkStableConnection, 100);
            }
          };
          checkStableConnection();
        });
        
        console.log("Successfully connected to LiveKit Cloud");
        console.log(
          "Connection established in:",
          Date.now() - (window as any).roomConnectionStart,
          "ms"
        );
        setRoom(newRoom);
      } catch (connectionError) {
        console.error("Failed to connect to LiveKit Cloud:", connectionError);

        // Log LiveKit connection failure to Sentry
        logCategorizedError(connectionError, {
          component: 'useVideoCall',
          operation: 'livekitConnect',
          callId: responseCallId,
          organizationId: currentOrganization?.id,
          metadata: {
            serverUrl,
            roomName,
            retryAttempts: 5,
            connectionTimeout: 15000,
            stabilityTimeout: 12000,
            roomState: newRoom?.state
          }
        });

        // Clear connection guards on error
        isConnectingRef.current = false;
        throw new Error(
          `Failed to connect to LiveKit Cloud: ${connectionError.message}`
        );
      }

      // Clear connection guards on successful connection
      isConnectingRef.current = false;
      
      const leadContactName = interviewDetails?.metadata?.variables?.lead_contact_name || interviewDetails?.metadata?.variable?.candidate_name;
      toast({
        title: "Video Call Started",
        description: `Connected to video call with ${leadContactName}`,
      });
    } catch (error) {
      console.error("Error initializing video call:", error);

      // Log video call initialization failure to Sentry
      logCategorizedError(error, {
        component: 'useVideoCall',
        operation: 'initializeVideoCall',
        callId: callState.callId,
        organizationId: currentOrganization?.id,
        metadata: {
          leadId,
          selectedAssistantId,
          isInterviewSession,
          hasCurrentOrganization: !!currentOrganization,
          callConfig
        }
      });

      // Clear connection guards on error
      isConnectingRef.current = false;

      setCallState((prev) => ({
        ...prev,
        connecting: false,
        error: "Failed to start video call",
      }));
      toast({
        title: "Call Failed",
        description: "Failed to start video call. Please try again.",
        variant: "destructive",
      });
    }
    // Don't set connecting to false here - let it stay true until agent speaks
  }, [
    lead,
    currentOrganization,
    selectedAssistantId,
    callConfig,
    callState.connecting,
    callState.connected,
    room,
    getCallServiceUrl,
    setupRoomEventListeners,
    toast,
    isInterviewSession,
    encodedParams,
  ]);

  const endCall = useCallback(
    async (endedReason: VideoCallEndReason = VIDEO_CALL_END_REASONS.USER_ENDED) => {
      if (!callState.callId) return;

      try {
        // Clear network monitoring immediately
        clearNetworkTimeout();

        // Don't update status/transcript from browser - let the Python agent handle it
        // The agent will set the proper ended_reason and save the authoritative transcript

        // Then end the call through the call service
        await CallService.endCall(callState.callId, currentOrganization?.id || "", endedReason);

        // Disconnect from LiveKit room
        if (room) {
          room.removeAllListeners();
          room.disconnect();
        }

        // Reset call state and network status
        setCallState((prev) => ({
          ...prev,
          connected: false,
          callActive: false,
          callId: null,
        }));
        setRoom(null);
        setRemoteParticipants([]);
        setNetworkStatus('connected'); // Reset network status

        // Don't close window immediately - let the InterviewEndedDialog handle it
        // window.close();

        toast({
          title: "Call Ended",
          description: "Video call has been ended successfully.",
        });
      } catch (error) {
        console.error("Error ending call:", error);

        // Log call ending failure to Sentry
        logCategorizedError(error, {
          component: 'useVideoCall',
          operation: 'endCall',
          callId: callState.callId,
          organizationId: currentOrganization?.id,
          metadata: {
            reason: endedReason,
            roomState: room?.state,
            wasCallActive: callState.callActive,
            endCallUrl: `${getCallServiceUrl()}${CALL_SERVICE_ENDPOINTS.END(callState.callId || '')}`
          }
        });

        toast({
          title: "Error",
          description: "Failed to end call properly. Please try again.",
          variant: "destructive",
        });
      }
    },
    [callState.callId, room, toast, getCallServiceUrl, clearNetworkTimeout]
  );

  // Cleanup effect for room event listeners and network monitoring
  useEffect(() => {
    return () => {
      // Clear network timeout on unmount
      clearNetworkTimeout();
      
      if (room) {
        console.log("Cleaning up room event listeners on unmount");
        room.removeAllListeners();
        if (room.state !== "disconnected") {
          room.disconnect();
        }
        setRoom(null);
        setRemoteParticipants([]);
      }
    };
  }, [room, clearNetworkTimeout]);

  return {
    // State
    callState,
    room,
    remoteParticipants,
    conversation,
    currentSubtitle,
    isAgentSpeaking,
    networkStatus,
    connectionQuality,

    // Actions
    initializeVideoCall,
    endCall,

    // Utils
    hasAutoStarted,
  };
};